import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { getCategories, adminCreateListing, type Category } from "@/utils/adminApi";
import request, { updateAdminListing } from "@/utils/api";
import DraggableImageGrid from "@/components/ui/DraggableImageGrid";

export default function AdminEditListingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    companyName: "",
    contactName: "",
    contactPhone: "",
    contactWechat: "",
    status: "pending",
    createdAt: "",
  });
  const [images, setImages] = useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // 加载分类数据
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        const activeCategories = data.filter((cat: Category) => cat.status === 'active');
        setCategories(activeCategories);
      } catch (err) {
        console.error("加载分类失败:", err);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // 加载帖子详情
  useEffect(() => {
    const loadListingDetail = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await request<any>(`/admin/listing-detail/${id}`);
        console.log('API返回数据:', data); // 调试日志
        if (!data || !data.id) {
          setNotFound(true);
          return;
        }
        setFormData({
          title: data.title || "",
          content: data.content || "",
          companyName: data.company_name || "",
          contactName: data.contact_name || "",
          contactPhone: data.contact_phone || "",
          contactWechat: data.contact_wechat || "",
          status: data.status || "pending",
          createdAt: data.created_at ? data.created_at.slice(0, 16) : "",
        });
        // 设置图片
        if (data.images && Array.isArray(data.images)) {
          setImages(data.images);
        }
        // 设置选中的分类（如果有 category_id）
        if (data.category_id) {
          setSelectedCategoryIds([data.category_id]);
        }
      } catch (err) {
        console.error('加载失败:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    loadListingDetail();
  }, [id]);

  // 处理分类标签选择
  const toggleCategory = (categoryId: string) => {
    setSelectedCategoryIds((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      }
      if (prev.length >= 5) {
        setError("最多只能选择5个分类");
        setTimeout(() => setError(""), 3000);
        return prev;
      }
      return [...prev, categoryId];
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 上传图片到服务器
  const uploadImage = async (base64Data: string): Promise<string> => {
    try {
      const response = await fetch(base64Data);
      const blob = await response.blob();
      const ext = blob.type.split('/')[1] || 'png';
      const filename = `image_${Date.now()}.${ext}`;
      
      const formData = new FormData();
      formData.append('file', blob, filename);
      
      const result = await request('/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (result && (result as any).url) {
        return (result as any).url;
      }
      if (result && (result as any).data && (result as any).data.url) {
        return (result as any).data.url;
      }
      console.warn("上传返回格式异常:", result);
      return base64Data;
    } catch (err) {
      console.error("图片上传失败:", err);
      return base64Data;
    }
  };

  // 下载远程图片（通过后端代理，绕过 CORS）
  const downloadImage = async (url: string): Promise<string> => {
    try {
      setUploadProgress(`正在下载图片: ${url.substring(0, 50)}...`);
      const result = await request('/admin/fetch-image', {
        params: { url },
      });
      if (result && (result as any).url) {
        return (result as any).url;
      }
      console.warn("图片下载返回异常:", result);
      return url;
    } catch (err) {
      console.error("下载图片失败:", err);
      return url;
    }
  };

  // 处理粘贴事件 - 支持图文一起粘贴
  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    let hasImages = false;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        hasImages = true;
        break;
      }
    }

    if (hasImages) {
      e.preventDefault();
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          if (images.length >= 6) {
            setError("最多上传6张图片");
            setTimeout(() => setError(""), 3000);
            break;
          }
          const file = items[i].getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
              const base64 = event.target?.result as string;
              setImages((prev) => [...prev, base64]);
            };
            reader.readAsDataURL(file);
          }
        }
      }
      return;
    }

    const html = clipboardData.getData("text/html");
    if (html) {
      const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
      const matches = [...html.matchAll(imgRegex)];
      
      if (matches.length > 0) {
        e.preventDefault();
        
        let plainText = clipboardData.getData("text/plain");
        
        const lines = plainText.split('\n');
        const filteredLines = lines.filter(line => {
          const trimmed = line.trim();
          if (/^[\w\-]+\.(png|jpg|jpeg|gif|webp|bmp)(\s*\.png)?$/i.test(trimmed)) {
            return false;
          }
          if (trimmed === '') return false;
          return true;
        });
        plainText = filteredLines.join('\n');
        
        const imageUrls = matches.map((m) => m[1]).filter((url) => url.startsWith("http"));
        
        if (images.length + imageUrls.length > 6) {
          setError(`最多上传6张图片，当前已选择${images.length}张`);
          setTimeout(() => setError(""), 3000);
          setFormData((prev) => ({
            ...prev,
            content: prev.content + plainText,
          }));
          return;
        }

        for (const url of imageUrls) {
          const downloadedImage = await downloadImage(url);
          setImages((prev) => [...prev, downloadedImage]);
        }

        setFormData((prev) => ({
          ...prev,
          content: prev.content + plainText,
        }));
        
        if (imageUrls.length > 0) {
          setTimeout(() => {
            setError("");
          }, 3000);
        }
      }
    }
  }, [images.length]);

  // 处理图片上传
  const handleImageUpload = () => {
    if (images.length >= 6) {
      setError("最多上传6张图片");
      setTimeout(() => setError(""), 3000);
      return;
    }
    
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;
      
      Array.from(files).forEach((file) => {
        if (images.length >= 6) return;
        
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          setImages((prev) => [...prev, base64]);
        };
        reader.readAsDataURL(file);
      });
    };
    input.click();
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // 从详情内容中提取图片URL
  const extractImagesFromContent = (content: string): string[] => {
    const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
    const matches = [...content.matchAll(imgRegex)];
    return matches.map((m) => m[1]);
  };

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (selectedCategoryIds.length === 0) {
      setError("请至少选择一个分类");
      return;
    }
    if (!formData.title.trim()) {
      setError("请输入标题");
      return;
    }
    if (!formData.content.trim()) {
      setError("请输入详情内容");
      return;
    }

    setIsSubmitting(true);
    setUploadProgress("正在上传图片...");
    
    try {
      const finalImages: string[] = [];
      
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        setUploadProgress(`正在上传图片 ${i + 1}/${images.length}...`);
        
        if (img.startsWith('data:')) {
          const uploadedUrl = await uploadImage(img);
          finalImages.push(uploadedUrl);
        } else {
          finalImages.push(img);
        }
      }

      setUploadProgress("正在保存信息...");
      
      await updateAdminListing(id!, {
        category_id: selectedCategoryIds[0] || undefined,
        title: formData.title,
        content: formData.content,
        images: finalImages.length > 0 ? finalImages : undefined,
        company_name: formData.companyName,
        contact_name: formData.contactName,
        contact_phone: formData.contactPhone,
        contact_wechat: formData.contactWechat,
        status: formData.status,
        created_at: formData.createdAt || undefined,
      });
      
      setSuccess(true);
    } catch (err) {
      console.error("保存失败:", err);
      setError("保存失败，请重试");
    } finally {
      setIsSubmitting(false);
      setUploadProgress("");
    }
  };

  // 加载中状态
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-500 text-sm mt-3">加载中...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // 未找到状态
  if (notFound) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <i className="ri-file-unknow-line text-4xl text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm mb-4">未找到该信息</p>
          <button onClick={() => navigate("/admin/listings")} className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg cursor-pointer">
            返回列表
          </button>
        </div>
      </AdminLayout>
    );
  }

  // 保存成功状态
  if (success) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mb-4">
            <i className="ri-check-line text-3xl text-green-500" />
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">保存成功！</h2>
          <p className="text-sm text-gray-500 mb-6">信息已更新</p>
          <button onClick={() => navigate("/admin/listings")} className="px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm cursor-pointer">
            返回信息列表
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/listings")} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
            <i className="ri-arrow-left-line text-gray-600 text-sm" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">编辑信息</h1>
            <p className="text-sm text-gray-500 mt-0.5">ID: {id}</p>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          {/* 左右两栏布局 */}
          <div className="grid grid-cols-2 gap-6">
            {/* 左侧 - 基本信息 */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
              <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3">
                <i className="ri-file-list-3-line mr-2 text-orange-500" />
                基本信息
              </h2>

              {/* 分类标签选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  选择分类 <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal ml-2">(最多选5个)</span>
                </label>
                {isLoadingCategories ? (
                  <div className="text-sm text-gray-400">加载中...</div>
                ) : categories.length === 0 ? (
                  <div className="text-sm text-gray-400">暂无可用分类</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => toggleCategory(cat.id)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          selectedCategoryIds.includes(cat.id)
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-gray-50 text-gray-600 border-gray-200 hover:border-orange-300 hover:bg-orange-50"
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
                {selectedCategoryIds.length > 0 && (
                  <p className="text-xs text-orange-500 mt-2">
                    已选择: {selectedCategoryIds.length} 个分类
                  </p>
                )}
              </div>

              {/* 标题 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  信息标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="例如：专业企业官网定制开发"
                  maxLength={100}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.title.length}/100</p>
              </div>

              {/* 公司 + 联系人 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">公司名称</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="请输入公司名称"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">联系人</label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="请输入联系人姓名"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>

              {/* 电话 + 微信 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">联系电话</label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="请输入联系电话"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">微信号</label>
                  <input
                    type="text"
                    name="contactWechat"
                    value={formData.contactWechat}
                    onChange={handleChange}
                    placeholder="请输入微信号"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>

              {/* 发布状态 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">发布状态</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                >
                  <option value="active">直接发布</option>
                  <option value="pending">待审核</option>
                  <option value="offline">草稿（不展示）</option>
                </select>
              </div>

              {/* 发布时间 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">发布时间</label>
                <input
                  type="datetime-local"
                  name="createdAt"
                  value={formData.createdAt}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                />
                <p className="text-xs text-gray-400 mt-1">留空则使用当前时间</p>
              </div>
            </div>

            {/* 右侧 - 详情内容和图片 */}
            <div className="space-y-5">
              {/* 详情内容 */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-gray-800">
                    <i className="ri-file-text-line mr-2 text-orange-500" />
                    详情内容 <span className="text-red-500">*</span>
                  </h2>
                  <span className="text-xs text-gray-400">支持粘贴图文</span>
                </div>
                <textarea
                  ref={contentRef}
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  onPaste={handlePaste}
                  placeholder="详细描述服务内容、优势、价格等信息...

💡 提示：从其他网站复制图文内容粘贴到这里，图片会自动下载并添加到下方图片列表中"
                  rows={12}
                  maxLength={5000}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400 resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.content.length}/5000</p>
              </div>

              {/* 图片上传 */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-gray-800">
                    <i className="ri-image-line mr-2 text-orange-500" />
                    图片附件
                  </h2>
                  <span className="text-xs text-gray-400">
                    {images.length}/6 张
                    {images.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setImages([])}
                        className="ml-2 text-orange-500 hover:text-orange-600"
                      >
                        清空
                      </button>
                    )}
                  </span>
                </div>
                
                {/* 从内容提取的图片提示 */}
                {extractImagesFromContent(formData.content).length > 0 && (
                  <div className="mb-3 p-2 bg-blue-50 rounded-lg text-xs text-blue-600">
                    <i className="ri-information-line mr-1" />
                    检测到详情内容中包含 {extractImagesFromContent(formData.content).length} 张图片
                  </div>
                )}
                
                <DraggableImageGrid
                  images={images}
                  onReorder={setImages}
                  onRemove={removeImage}
                  onAdd={handleImageUpload}
                />
                <p className="text-xs text-gray-400 mt-3">
                  <i className="ri-information-line mr-1" />
                  支持粘贴、点击上传或拖拽排序，第一张为封面图，最多6张
                </p>
              </div>
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/admin/listings")}
              className="px-6 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm cursor-pointer"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2.5 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 text-sm cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  {uploadProgress || "保存中..."}
                </span>
              ) : (
                "保存修改"
              )}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
